import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Circle, GraduationCap, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useFeatureToggles } from '@/hooks/useFeatureToggles';
import { FormationApi } from '@/api/formation';
import { TutorialButton } from '@/features/tutorials';
import { visibleSteps } from './steps';
import { FormationTeamProgress } from './FormationTeamProgress';

const HIDE_KEY = 'vp_formation_checklist_masquee';

const readHidden = (): boolean => {
  try {
    return localStorage.getItem(HIDE_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Checklist de prise en main (flag opt-in `formation_checklist`) : étapes cochées
 * automatiquement d'après les vraies données du commerce, lien direct vers l'écran.
 */
export function FormationChecklistCard({ className = '' }: { className?: string }) {
  const { isFeatureEnabled } = useFeatureToggles();
  const enabled = isFeatureEnabled('formation_checklist');
  const [hidden, setHidden] = useState(readHidden);
  const { data } = useQuery({
    queryKey: ['formation-progress'],
    enabled: enabled && !hidden,
    staleTime: 60_000,
    queryFn: async () => {
      const r = await FormationApi.getProgress();
      return r.ok ? r.value : null;
    },
  });

  if (!enabled || hidden || !data) return null;
  const { steps, done, total } = visibleSteps(data);

  const hide = () => {
    try {
      localStorage.setItem(HIDE_KEY, '1');
    } catch {
      /* préférence locale seulement */
    }
    setHidden(true);
  };

  return (
    <section className={`rounded-lg border bg-card p-4 ${className}`}>
      <div className="flex items-center gap-2">
        <GraduationCap className="h-5 w-5 text-primary" />
        <h2 className="flex-1 font-semibold">
          Prise en main · {done}/{total}
        </h2>
        <TutorialButton tutoId="visionpos-tuto-premier-jour" />
        <Button size="icon" variant="ghost" aria-label="Masquer" onClick={hide}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      <Progress value={(done / Math.max(total, 1)) * 100} className="mt-2 h-2" />
      <ul className="mt-3 grid gap-2 sm:grid-cols-2">
        {steps.map((s) => (
          <li key={s.key} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
            {data[s.key] ? (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
            ) : (
              <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <div className="min-w-0 flex-1">
              <div className={data[s.key] ? 'text-muted-foreground line-through' : 'font-medium'}>
                {s.title}
              </div>
              <div className="text-xs text-muted-foreground">{s.hint}</div>
            </div>
            {s.tutoId && <TutorialButton tutoId={s.tutoId} compact />}
            {!data[s.key] && (
              <Button asChild size="sm" variant="outline">
                <Link to={s.route}>Y aller</Link>
              </Button>
            )}
          </li>
        ))}
      </ul>
      {data.manager && <FormationTeamProgress />}
    </section>
  );
}
